import { useNotification, useUpdate } from "@refinedev/core";
import { Button, Drawer, Form, Input } from "antd";
import { useEffect, type FC } from "react";
import type { Project } from "../../../types";

interface EditProjectDrawerProps {
  project: Project | null;
  onClose: () => void;
  isMobile: boolean;
}

export const EditProjectDrawer: FC<EditProjectDrawerProps> = ({ project, onClose, isMobile }) => {
  const [form] = Form.useForm();
  const { open: notify } = useNotification();
  const { mutate: updateProject, mutation: { isPending: updating } } = useUpdate<Project>();

  useEffect(() => {
    if (project) form.setFieldsValue({ name: project.name });
  }, [project, form]);

  const handleClose = () => {
    onClose();
    form.resetFields();
  };

  const handleSubmit = (values: { name: string }) => {
    if (!project) return;
    updateProject(
      { resource: "projects", id: project.id, values: { name: values.name } },
      {
        onSuccess: handleClose,
        onError: (error) => {
          if (error.statusCode === 409) {
            notify?.({ type: "error", message: "A project with this name already exists" });
          } else {
            notify?.({ type: "error", message: "Failed to rename project" });
          }
        },
      }
    );
  };

  return (
    <Drawer title="Edit project" open={Boolean(project)} onClose={handleClose} width={isMobile ? "100%" : 480}>
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="name"
          label="Project name"
          rules={[{ required: true, message: "Name is required" }]}
        >
          <Input placeholder="e.g. My Client Project" />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={updating} block>
          Save changes
        </Button>
      </Form>
    </Drawer>
  );
};
